import { useEffect, FC } from 'react';
import {
    Controller,
    SubmitErrorHandler,
    SubmitHandler,
    useForm
} from 'react-hook-form';
import { useLazyOptimizelyExperiment } from '@zoopla/optimizely';
import { InputField } from '@zoopla-eng/zds-components/input-field';
import { Button } from '@zoopla-eng/zds-components/button';
import { Box } from '@zoopla-eng/zds-components/box';
import triggerTracking from '@blueprint/tracking';
import { useDispatch } from 'react-redux';
import { Maybe } from 'graphql/jsutils/Maybe';

import { setLoggedIn, setUser } from '@zoopla/redux-reducer-user';
import useRegistration from '../../hooks/useRegistration';
import RegistrationFormData from '../../interfaces/RegistrationFormData';
import { trackAuthSubmit } from '../../tracking';
import { EMAIL_REGEX, PASSWORD_MIN_LENGTH } from '../../validation/rules';
import { ReassuranceText, StyledGeneralError } from '../../AuthModal.styles';
import { AuthModalStepKeys, SocialAuthProps } from '../../AuthModal.types';
import { transformFormData, hasNthErrors } from '../../utils';
import SocialAuth from '../SignIn/SocialAuth';
import { User } from '../../../../graphql.generated';
import { liverampAts } from '../../utils/liverampAts';
import { InputPassword } from '../../InputPassword';
import { EmailPreferencesToggle } from '../../common/EmailPreferencesToggle';
import { AboutYou } from '../../common/AboutYou';
import { useOptimizelyFeatureFlag } from '@zoopla/optimizely';

const detailsPasswordFieldGuidance = 'Minimum 8 characters';
const socialCopy = {
    cta: 'or continue with your email',
    facebook: 'Facebook',
    google: 'Google'
};

type RegistrationFormProps = Pick<
    SocialAuthProps,
    | 'close'
    | 'actionAfterAuth'
    | 'resetActionAfterAuth'
    | 'onSocialAuth'
    | 'setCurrentStep'
> & {
    shouldFetchUser?: boolean;
};

const RegisterForm: FC<React.PropsWithChildren<RegistrationFormProps>> = ({
    close,
    actionAfterAuth,
    resetActionAfterAuth = () => null,
    onSocialAuth,
    setCurrentStep
}) => {
    const [isLoadingFlag, isFlagEnabled] = useLazyOptimizelyExperiment(
        'crme-2002-your-situation-reg-experiment'
    );
    const [useLiveRampAts] = useOptimizelyFeatureFlag(
        'client-cmp-108-liveramp-ats'
    );

    const {
        register,
        handleSubmit,
        setError,
        formState: { errors, isSubmitting },
        reset,
        getFieldState,
        getValues,
        control
    } = useForm<RegistrationFormData>({
        mode: 'onBlur',
        reValidateMode: 'onBlur'
    });

    const { registerUser, generalError, resetGeneralError } =
        useRegistration(setError);

    const reduxDispatch = useDispatch();

    // called after successful submission of the form
    const onRegisterSuccess = async (user?: User) => {
        localStorage.setItem('new_register_user', 'true');
        reduxDispatch(setLoggedIn(true));
        resetActionAfterAuth();

        if (!isLoadingFlag && isFlagEnabled === 'enabled') {
            setCurrentStep?.(AuthModalStepKeys.YOUR_SITUATION);
            return;
        }

        await actionAfterAuth?.action();
        close(user);
    };

    const isDefined = <T,>(x: Maybe<T>): x is T =>
        x !== undefined && x !== null;

    const onSocialAuthSuccess = async (data?: User) => {
        if (data) {
            if (useLiveRampAts && isDefined(data.email)) {
                liverampAts(data.email);
            }

            reduxDispatch(setUser(data));
        }

        if (onSocialAuth) {
            onSocialAuth(data);
        } else {
            close();
        }
    };

    const passedActionAfterAuth = {
        action: onSocialAuthSuccess,
        eventLabel: actionAfterAuth?.eventLabel || 'register_modal',
        segment: actionAfterAuth?.segment
    };

    const resetFormErrors = () => {
        // reset signin form general and field-specific errors
        resetGeneralError();

        const { consent__insights__24, ...currentValues } = getValues();
        const consentState = getFieldState('consent__insights__24');

        reset({
            ...currentValues,
            ...(consentState.isTouched &&
                !consentState.error && {
                    consent__insights__24
                })
        });
    };

    const onFormSubmit: SubmitHandler<RegistrationFormData> = async (
        formData
    ) => {
        const payload = await registerUser(transformFormData(formData));

        if (!payload?.data?.registerUser) {
            return;
        }

        if (payload.data.registerUser.success) {
            if (useLiveRampAts) {
                liverampAts(formData.email);
            }

            onRegisterSuccess();
        }

        trackAuthSubmit({
            payload: payload.data.registerUser,
            formData,
            eventLabel: actionAfterAuth?.eventLabel,
            segment: actionAfterAuth?.segment
        });
    };

    const onFormSubmitError: SubmitErrorHandler<RegistrationFormData> = (
        errors
    ) => {
        if (
            errors.consent__insights__24 &&
            hasNthErrors({ expectedErrors: 1, formErrors: errors })
        ) {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'registration',
                eventAction: 'failure_must_select_opt_in',
                eventLabel: actionAfterAuth?.eventLabel
            });
        }
    };

    const onSubmit = handleSubmit(onFormSubmit, onFormSubmitError);

    useEffect(() => {
        reset();
    }, [reset]);

    return (
        <>
            <SocialAuth
                copy={socialCopy}
                type="register"
                shouldReset={isSubmitting}
                resetErrors={resetFormErrors}
                actionAfterAuth={passedActionAfterAuth}
            />
            <form
                onSubmit={onSubmit}
                noValidate
                data-testid="auth-modal-registration-form"
                method="post"
            >
                <div role="alert">
                    {generalError && (
                        <Box pb="$S500">
                            <StyledGeneralError data-testid="general-error">
                                Sorry, something went wrong. Please try again.
                            </StyledGeneralError>
                        </Box>
                    )}
                </div>

                <Box pb="$S500">
                    <InputField
                        label="Email address"
                        aria-required
                        type="email"
                        autoComplete="email"
                        errorText={
                            errors.email &&
                            (errors.email.message ||
                                'Please enter a valid email address')
                        }
                        {...register('email', {
                            required: true,
                            pattern: EMAIL_REGEX
                        })}
                    />
                </Box>
                <Box pb="$M100" bpM100={{ pb: '$M300' }}>
                    <InputPassword
                        autoComplete="new-password"
                        label="Create password"
                        aria-required
                        helpText={detailsPasswordFieldGuidance}
                        errorText={
                            errors.password &&
                            (errors.password.message ||
                                detailsPasswordFieldGuidance)
                        }
                        {...register('password', {
                            required: true,
                            minLength: PASSWORD_MIN_LENGTH
                        })}
                    />
                </Box>

                {!isLoadingFlag && isFlagEnabled !== 'enabled' && (
                    <Box pb="$M100" bpM100={{ pb: '$M300' }}>
                        <Controller
                            control={control}
                            name="situation"
                            render={({ field: { onChange, value, ref } }) => (
                                <AboutYou
                                    label="What can Zoopla help you with? (optional)"
                                    onChange={onChange}
                                    value={value}
                                    ref={ref}
                                />
                            )}
                        />
                    </Box>
                )}

                <Box pb="$M100" bpM100={{ pb: '$S500' }}>
                    <Controller
                        control={control}
                        name="consent__insights__24"
                        rules={{ required: true }}
                        render={({
                            field: { onChange, name, ref, onBlur }
                        }) => (
                            <EmailPreferencesToggle
                                legend="Email preferences"
                                label="I want to hear about the latest property news, advice and new home developments from Zoopla and its selected partners only."
                                errorText={
                                    errors.consent__insights__24 &&
                                    'Please set your email preferences.'
                                }
                                name={name}
                                onChange={onChange}
                                onBlur={onBlur}
                                ref={ref}
                            />
                        )}
                    />
                </Box>

                <Box pb="$M100">
                    <ReassuranceText data-testid="unsubscribe-text">
                        You can unsubscribe or update your preferences at any
                        time.
                    </ReassuranceText>
                </Box>

                <Box pb="$S500" display="flex" justifyContent="center">
                    <Box width="100%" bpM100={{ width: '50%' }}>
                        <Button
                            category="primary"
                            loading={isSubmitting}
                            loadingText="Registering"
                            data-testid="register-button"
                            fw
                        >
                            Register
                        </Button>
                    </Box>
                </Box>
            </form>
        </>
    );
};

export default RegisterForm;
